import clsx from 'clsx'
import { type ReactNode } from 'react'

export type ProgressCircleSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg'
export type ProgressCircleShape = 'circle' | 'half-circle'

export interface ProgressCircleProps {
    value: number
    min?: number
    max?: number
    size?: ProgressCircleSize
    shape?: ProgressCircleShape
    label?: ReactNode
    showValue?: boolean
    formatValue?: (percent: number) => ReactNode
    children?: ReactNode
    'aria-label'?: string
    id?: string
    className?: string
}

interface SizeConfig {
    outer: number
    stroke: number
    valueClass: string
    labelClass: string
}

const SIZE_CONFIG: Record<ProgressCircleSize, SizeConfig> = {
    xxs: { outer: 64, stroke: 6, valueClass: 'text-sm', labelClass: 'text-xs' },
    xs: { outer: 160, stroke: 16, valueClass: 'text-display-xs', labelClass: 'text-xs' },
    sm: { outer: 200, stroke: 20, valueClass: 'text-display-sm', labelClass: 'text-xs' },
    md: { outer: 240, stroke: 24, valueClass: 'text-display-md', labelClass: 'text-sm' },
    lg: { outer: 280, stroke: 28, valueClass: 'text-display-lg', labelClass: 'text-sm' },
}

const CENTER_POSITION_CLASSES: Record<ProgressCircleShape, string> = {
    circle: 'inset-0 justify-center',
    'half-circle': 'inset-x-0 bottom-2 justify-end',
}

const XXS_VALUE_POSITION_CLASSES: Record<ProgressCircleShape, string> = {
    circle: 'inset-0 items-center',
    'half-circle': 'inset-x-0 bottom-0.5 items-end',
}

const XXS_ROOT_GAP_CLASSES: Record<ProgressCircleShape, string> = {
    circle: 'gap-spacing-xs',
    'half-circle': 'gap-spacing-none',
}

const TRACK_CLASS = 'stroke-background-bg-quaternary'
const ARC_CLASS = 'stroke-background-bg-brand-solid transition-[stroke-dashoffset] duration-300'
const VALUE_CLASS = 'font-semibold text-text-primary'
const LABEL_CLASS = 'font-medium text-text-tertiary'

const defaultFormatValue = (percent: number): ReactNode => `${Math.round(percent)}%`

const toValueText = (node: ReactNode): string | undefined => {
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node)
    }
    return undefined
}

interface RingProps {
    config: SizeConfig
    shape: ProgressCircleShape
    percent: number
}

const Ring = ({ config, shape, percent }: RingProps) => {
    const { outer, stroke } = config
    const radius = (outer - stroke) / 2
    const center = outer / 2

    if (shape === 'circle') {
        const circumference = 2 * Math.PI * radius
        const dashOffset = circumference * (1 - percent / 100)
        return (
            <svg
                width={outer}
                height={outer}
                viewBox={`0 0 ${outer} ${outer}`}
                className="block -rotate-90"
                aria-hidden>
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    strokeWidth={stroke}
                    className={TRACK_CLASS}
                />
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    className={ARC_CLASS}
                />
            </svg>
        )
    }

    const height = center + stroke / 2
    const arcLength = Math.PI * radius
    const dashOffset = arcLength * (1 - percent / 100)
    const path = `M ${center - radius} ${center} A ${radius} ${radius} 0 0 1 ${center + radius} ${center}`

    return (
        <svg
            width={outer}
            height={height}
            viewBox={`0 0 ${outer} ${height}`}
            className="block"
            aria-hidden>
            <path
                d={path}
                fill="none"
                strokeWidth={stroke}
                strokeLinecap="round"
                className={TRACK_CLASS}
            />
            <path
                d={path}
                fill="none"
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={arcLength}
                strokeDashoffset={dashOffset}
                className={ARC_CLASS}
            />
        </svg>
    )
}

export const ProgressCircle = ({
    value,
    min = 0,
    max = 100,
    size = 'md',
    shape = 'circle',
    label,
    showValue = true,
    formatValue = defaultFormatValue,
    children,
    'aria-label': ariaLabel,
    id,
    className,
}: ProgressCircleProps) => {
    const clampedValue = Math.max(min, Math.min(max, value))
    const percent = max === min ? 0 : ((clampedValue - min) / (max - min)) * 100
    const formattedValue = formatValue(percent)
    const config = SIZE_CONFIG[size]
    const hasChildren = children !== undefined && children !== null
    const hasLabel = label !== undefined && label !== null && label !== false

    const a11yProps = {
        role: 'progressbar' as const,
        'aria-valuemin': min,
        'aria-valuemax': max,
        'aria-valuenow': clampedValue,
        'aria-valuetext': toValueText(formattedValue),
        'aria-label': ariaLabel,
        id,
    }

    if (size === 'xxs') {
        return (
            <div
                {...a11yProps}
                className={clsx(
                    'inline-flex flex-col items-center font-body',
                    XXS_ROOT_GAP_CLASSES[shape],
                    className,
                )}>
                <div className="relative">
                    <Ring config={config} shape={shape} percent={percent} />
                    <div
                        className={clsx(
                            'absolute flex justify-center',
                            XXS_VALUE_POSITION_CLASSES[shape],
                        )}>
                        {hasChildren && children}
                        {!hasChildren && showValue && (
                            <span className={clsx(config.valueClass, VALUE_CLASS)}>
                                {formattedValue}
                            </span>
                        )}
                    </div>
                </div>
                {!hasChildren && hasLabel && (
                    <span className={clsx('w-full text-center', config.labelClass, LABEL_CLASS)}>
                        {label}
                    </span>
                )}
            </div>
        )
    }

    return (
        <div {...a11yProps} className={clsx('relative inline-flex font-body', className)}>
            <Ring config={config} shape={shape} percent={percent} />
            <div
                className={clsx(
                    'absolute flex flex-col items-center text-center',
                    CENTER_POSITION_CLASSES[shape],
                )}>
                {hasChildren && children}
                {!hasChildren && hasLabel && (
                    <span className={clsx(config.labelClass, LABEL_CLASS)}>{label}</span>
                )}
                {!hasChildren && showValue && (
                    <span className={clsx(config.valueClass, VALUE_CLASS)}>{formattedValue}</span>
                )}
            </div>
        </div>
    )
}

ProgressCircle.displayName = 'ProgressCircle'
